import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { documentEvidenceHttp } from '@/services/documents'
import { returnCasesHttp } from '@/services/returnCases'
import type { ChecklistSlot, TaxFact } from '@/types/returnCase.types'
import type { EvidenceDocumentVersion } from '@/types/document.types'

interface Props {
  caseId: string
  customerId: string
  assessmentYear: string
  currentRevision: number
  checklist: ChecklistSlot[]
  facts: TaxFact[]
  canUpload: boolean
  canLink: boolean
  onChanged: () => Promise<void>
}

export default function EvidenceRailPanel({
  caseId, customerId, assessmentYear, currentRevision, checklist, facts, canUpload, canLink, onChanged,
}: Props) {
  const queryClient = useQueryClient()
  const rail = useQuery({
    queryKey: ['return-case-evidence', caseId],
    queryFn: () => returnCasesHttp.evidenceRail(caseId),
  })
  const [file, setFile] = useState<File | null>(null)
  const [slotId, setSlotId] = useState('')
  const [factId, setFactId] = useState('')
  const [pending, setPending] = useState<{ documentId: string; version: EvidenceDocumentVersion } | null>(null)
  const [replacementBindingId, setReplacementBindingId] = useState('')
  const [page, setPage] = useState(1)
  const [region, setRegion] = useState({ x: 0, y: 0, width: 1, height: 1 })
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewAlt, setPreviewAlt] = useState('')
  const [error, setError] = useState('')
  const [overrideReason, setOverrideReason] = useState('Reviewed against the customer profile and confirmed as the correct evidence.')

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])

  const revision = rail.data?.case_revision ?? currentRevision
  const activeBindings = rail.data?.bindings.filter(item => item.status === 'ACTIVE') ?? []
  const activeLinks = rail.data?.links.filter(item => item.status === 'ACTIVE') ?? []
  const uploadSlots = checklist.filter(item => item.kind === 'UPLOAD' && item.status !== 'NOT_APPLICABLE')

  const refreshAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['return-case-evidence', caseId] }),
      onChanged(),
    ])
  }

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a file first')
      if (replacementBindingId) {
        const binding = activeBindings.find(item => item.id === replacementBindingId)
        if (!binding) throw new Error('Choose an active version to replace')
        const version = await documentEvidenceHttp.replace({
          documentId: binding.document_id, replacesVersionId: binding.document_version_id, file,
        })
        return { documentId: binding.document_id, version }
      }
      const result = await documentEvidenceHttp.upload({
        file, category: 'other', customerId, assessmentYear,
        description: 'Individual return preparation evidence',
      })
      return { documentId: result.document_id, version: result.version }
    },
    onSuccess: result => { setPending(result); setError('') },
    onError: value => setError(value instanceof Error ? value.message : 'Upload failed'),
  })

  const refreshVersion = useMutation({
    mutationFn: async () => {
      if (!pending) throw new Error('No processing version')
      const versions = await documentEvidenceHttp.versions(pending.documentId)
      const version = versions.find(item => item.id === pending.version.id)
      if (!version) throw new Error('Version history no longer contains this upload')
      return { documentId: pending.documentId, version }
    },
    onSuccess: result => { setPending(result); setError('') },
    onError: value => setError(value instanceof Error ? value.message : 'Status refresh failed'),
  })

  const bind = useMutation({
    mutationFn: async () => {
      if (!pending || pending.version.state !== 'AVAILABLE' || !slotId) {
        throw new Error('Choose a checklist slot and wait for an AVAILABLE version')
      }
      const prior = activeBindings.find(item => item.checklist_slot_id === slotId)
      return returnCasesHttp.bindChecklistEvidence(caseId, {
        expected_revision: revision, checklist_slot_id: slotId,
        document_id: pending.documentId, document_version_id: pending.version.id,
        supersedes_binding_id: prior?.id,
        reason: prior ? 'Replace checklist evidence with a reviewed immutable version' : 'Attach reviewed evidence to checklist slot',
      })
    },
    onSuccess: async () => { setPending(null); setFile(null); setError(''); await refreshAll() },
    onError: async value => { setError(value instanceof Error ? value.message : 'Binding failed'); await refreshAll() },
  })

  const overrideMismatch = useMutation({
    mutationFn: async () => {
      if (!pending) throw new Error('No quarantined version selected')
      return documentEvidenceHttp.overrideMismatch(
        pending.documentId, pending.version.id, overrideReason,
      )
    },
    onSuccess: version => { if (pending) setPending({ ...pending, version }); setError('') },
    onError: value => setError(value instanceof Error ? value.message : 'Mismatch override was denied'),
  })

  const linkFact = useMutation({
    mutationFn: async () => {
      const binding = activeBindings.find(item => item.id === replacementBindingId) ?? activeBindings[0]
      if (!binding || !factId) throw new Error('Choose a fact and active checklist evidence')
      return returnCasesHttp.linkFactEvidence(caseId, factId, {
        expected_revision: revision, binding_id: binding.id, evidence_role: 'PRIMARY',
        location: { coordinate_schema: 'evidence-location-v1', page, rectangle: region },
        reason: 'Link reviewed source page and normalized region',
      })
    },
    onSuccess: async () => { setError(''); await refreshAll() },
    onError: async value => { setError(value instanceof Error ? value.message : 'Evidence link failed'); await refreshAll() },
  })

  const openPreview = async (linkId: string) => {
    const link = activeLinks.find(item => item.id === linkId)
    if (!link) return
    try {
      const grant = await documentEvidenceHttp.previewGrant(
        caseId, link.document_id, link.document_version_id, link.location.page,
      )
      if (!grant.preview_available) {
        setError('Page preview is unavailable. The accessible original-download fallback remains available.')
        return
      }
      const blob = await documentEvidenceHttp.redeem(grant.access_url)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setPreviewUrl(URL.createObjectURL(blob))
      setPreviewAlt(`Evidence page ${link.location.page} for fact ${link.fact_id}`)
      if (link.location.rectangle) setRegion(link.location.rectangle)
      setError('')
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Preview could not be opened')
    }
  }

  const downloadFallback = async (documentId: string, versionId: string) => {
    try {
      const grant = await documentEvidenceHttp.downloadGrant(caseId, documentId, versionId)
      const blob = await documentEvidenceHttp.redeem(grant.access_url)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'evidence'
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (value) {
      setError(value instanceof Error ? value.message : 'Download could not be opened')
    }
  }

  return (
    <section aria-labelledby="evidence-rail-title">
      <h2 id="evidence-rail-title" className="font-semibold">Evidence</h2>
      <p className="mt-1 text-xs text-text-secondary">Immutable versions · exact source locations</p>
      {error && <div className="alert alert-error mt-3 text-xs" role="alert">{error}</div>}

      {canUpload && <div className="mt-4 space-y-3 rounded border border-border p-3">
        <label className="block text-xs" htmlFor="evidence-file">Upload or replacement file</label>
        <input id="evidence-file" type="file" className="block w-full text-xs" onChange={event => setFile(event.target.files?.[0] ?? null)} />
        <label className="block text-xs" htmlFor="replacement-binding">Replacement target</label>
        <select id="replacement-binding" className="input w-full text-xs" value={replacementBindingId} onChange={event => setReplacementBindingId(event.target.value)}>
          <option value="">New document</option>
          {activeBindings.map(item => <option key={item.id} value={item.id}>Version {item.document_version_number} · {item.document_id.slice(0, 8)}</option>)}
        </select>
        <button className="btn btn-secondary w-full" disabled={!file || upload.isPending} onClick={() => upload.mutate()}>Upload immutable version</button>
        {pending && <div className="rounded bg-surface-secondary p-2 text-xs">
          <div><strong>Status:</strong> {pending.version.state}</div>
          <div className="break-all">Version {pending.version.version_number} · {pending.version.id}</div>
          {pending.version.quarantine_code && <div className="text-error">Review: {pending.version.quarantine_code}</div>}
          <button className="link mt-2" onClick={() => refreshVersion.mutate()}>Refresh processing status</button>
          {canLink && pending.version.quarantine_code === 'PAN_OR_PERIOD_MISMATCH' && <div className="mt-2">
            <label className="block" htmlFor="override-reason">Professional override reason</label>
            <textarea id="override-reason" className="input mt-1 w-full" value={overrideReason} onChange={event => setOverrideReason(event.target.value)} />
            <button className="btn btn-secondary mt-2" onClick={() => overrideMismatch.mutate()}>Override reviewed mismatch</button>
          </div>}
        </div>}
        <label className="block text-xs" htmlFor="checklist-slot">Checklist slot</label>
        <select id="checklist-slot" className="input w-full text-xs" value={slotId} onChange={event => setSlotId(event.target.value)}>
          <option value="">Choose a slot</option>
          {uploadSlots.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
        </select>
        <button className="btn btn-primary w-full" disabled={pending?.version.state !== 'AVAILABLE' || !slotId || bind.isPending} onClick={() => bind.mutate()}>Bind available evidence</button>
      </div>}

      <div className="mt-4">
        <h3 className="text-sm font-medium">Checklist coverage</h3>
        <ul className="mt-2 space-y-2 text-xs">
          {uploadSlots.map(slot => {
            const binding = activeBindings.find(item => item.checklist_slot_id === slot.id)
            return <li key={slot.id} className="rounded border border-border p-2"><div>{slot.label}</div><div className="text-text-secondary">{binding ? `Covered by immutable v${binding.document_version_number}` : 'Open — no available evidence'}</div></li>
          })}
          {uploadSlots.length === 0 && <li className="text-text-secondary">No upload slots have been generated.</li>}
        </ul>
      </div>

      {canLink && activeBindings.length > 0 && facts.length > 0 && <div className="mt-4 space-y-2 rounded border border-border p-3 text-xs">
        <h3 className="text-sm font-medium">Link exact fact source</h3>
        <label htmlFor="evidence-fact">Fact</label>
        <select id="evidence-fact" className="input w-full text-xs" value={factId} onChange={event => setFactId(event.target.value)}><option value="">Choose a fact</option>{facts.map(item => <option key={item.id} value={item.id}>{item.fact_key}</option>)}</select>
        <label htmlFor="evidence-page">Page</label><input id="evidence-page" className="input w-full" type="number" min={1} value={page} onChange={event => setPage(Number(event.target.value))} />
        <fieldset><legend>Normalized rectangle</legend><div className="grid grid-cols-2 gap-1">{(['x', 'y', 'width', 'height'] as const).map(key => <label key={key}>{key}<input aria-label={`Region ${key}`} className="input w-full" type="number" min={0} max={1} step="0.01" value={region[key]} onChange={event => setRegion(current => ({ ...current, [key]: Number(event.target.value) }))} /></label>)}</div></fieldset>
        <button className="btn btn-primary w-full" onClick={() => linkFact.mutate()}>Link reviewed page and region</button>
      </div>}

      <div className="mt-4">
        <h3 className="text-sm font-medium">Linked sources</h3>
        <ul className="mt-2 space-y-2 text-xs">{activeLinks.map(link => <li key={link.id} className="rounded border border-border p-2"><div>Fact {link.fact_id.slice(0, 8)} · page {link.location.page}</div><div className="mt-1 flex flex-wrap gap-2"><button className="link" onClick={() => openPreview(link.id)}>View exact source</button><button className="link" onClick={() => downloadFallback(link.document_id, link.document_version_id)}>Accessible original</button></div></li>)}</ul>
      </div>

      {previewUrl && <figure className="mt-4"><div className="relative overflow-hidden rounded border border-border"><img className="block w-full" src={previewUrl} alt={previewAlt} /><span aria-hidden="true" className="absolute border-2 border-red-600 bg-red-200/20" style={{ left: `${region.x * 100}%`, top: `${region.y * 100}%`, width: `${region.width * 100}%`, height: `${region.height * 100}%` }} /></div><figcaption className="mt-1 text-xs">Exact linked region. If the preview is unavailable, use Accessible original.</figcaption></figure>}
    </section>
  )
}
